import {useMutation} from "@tanstack/react-query";
import {getLocationFromZip} from "api/location.api.ts";
import {privateGroup} from "conts/profileConst.js";
import {state_abbr} from "conts/stateAbbreviation.js";
import {useToast} from "hooks/index.js";
import React, {createContext, useContext, useEffect, useState} from "react";
import {toUpperCaseFirstLetterOfEachWord} from "utils/index.js";
import {useAuth} from "./AuthContext.jsx";

const UpdateTeacherContext = createContext({});

export const UpdateTeacherProvider = ({children}) => {
    const {userData} = useAuth();
    const initializeData = (userData) => {
        const {teacher = {}, gallery = []} = userData || {};

        const images =
            gallery?.map((img, index) => ({
                id: `${new Date().getTime()}_${index}`,
                name: "",
                type: "",
                uri: img.name,
                uploaded: true,
                deleted: false,
            })) || [];

        return {
            profile_pic: {
                name: "",
                type: "",
                uri: teacher?.profile_pic || "",
                uploaded: !!teacher?.profile_pic,
            },
            zip_code: teacher?.zip_code || "",
            city: teacher?.city || "",
            state: teacher?.state || "",
            lat: teacher?.lat || "",
            lng: teacher?.lng || "",
            subject_want_to_teach: teacher?.subject_want_to_teach || "",
            subject_sub_category: teacher?.subject_sub_category || "",
            locations_of_class: teacher?.locations_of_class || "",
            experience_on_subject: teacher?.experience_on_subject || "",
            expected_students: teacher?.expected_students || "",
            expected_students_online: teacher?.expected_students_online || "",
            level_of_student: teacher?.level_of_student || "",
            student_need_to_have: teacher?.student_need_to_have || "",
            student_need_to_have_online: teacher?.student_need_to_have_online || "",
            cancellation_policy: teacher?.cancellation || [],
            discount_policy_for_private_class:
                teacher?.discount_policy_for_private_class || [],
            gallery: images,
            number_of_students: teacher?.number_of_students || 2,
            number_of_students_private_class:
                teacher?.number_of_students_private_class || 1,
            price_per_group_class: teacher?.price_per_group_class || "",
            price_per_group_class_estimate_earning:
                teacher?.price_per_group_class_estimate_earning || 0,
            price_per_private_class: teacher?.price_per_private_class || "",
            price_per_private_class_estimate_earning:
                teacher?.price_per_private_class_estimate_earning || 0,
            price_per_online_class: teacher?.price_per_online_class || "",
            price_per_online_class_estimate_earning: teacher?.price_per_online_class_estimate_earning || 0,
            profile_completion: teacher?.profile_completion || "",
            type_of_class: teacher?.type_of_class || privateGroup,
            neighborhood: teacher?.neighborhood || "",
            intro_video_url: teacher?.intro_video_url || "",
            enlist_status: teacher.enlist_status || "",
            online_type: teacher?.online_type || ""
        };
    };

    const [formData, setFormData] = useState(initializeData(userData));

    useEffect(() => {
        if (userData) {
            setFormData(initializeData(userData));
        }
    }, [userData]);

    const {mutate: getLocation} = useMutation({
        mutationFn: getLocationFromZip,
        onSuccess: (data) => {
            if (data.features?.length < 1) {
                useToast("error", "Enter a valid Zip Code");
                changeFormData("city", "");
                changeFormData("state", "");
                changeFormData("lat", "");
                changeFormData("lng", "");
                return;
            }
            changeFormData(
                "city",
                toUpperCaseFirstLetterOfEachWord(data.features[0].properties.city)
            );
            changeFormData("state", state_abbr[data.features[0].properties.state]);

            let long = data?.features[0]?.geometry?.coordinates?.[0]?.[0]?.[0]?.[0];
            let lat = data?.features[0]?.geometry?.coordinates?.[0]?.[0]?.[0]?.[1];
            if (!long) {
                long = data?.features[0]?.geometry?.coordinates?.[0]?.[0]?.[0];
                lat = data?.features[0]?.geometry?.coordinates?.[0]?.[0]?.[1];
            }
            changeFormData("lat", `${lat}`);
            changeFormData("lng", `${long}`);
        },
        onError: (error) => {
            useToast("error", "Enter correct Zip Code");
            changeFormData("city", "");
            changeFormData("state", "");
        },
    });

    const changeFormData = (key, value) => {
        if (key === "zip_code" && value?.length === 5) {
            getLocation(value);
        }
        setFormData((prevState) => {
            return {
                ...prevState,
                [key]: value,
            };
        });
    };

    return (
        <UpdateTeacherContext.Provider
            value={{formData, changeFormData, setFormData}}
        >
            {children}
        </UpdateTeacherContext.Provider>
    );
};

export const useUpdateTeacher = () => {
    const context = useContext(UpdateTeacherContext);
    if (!context) {
        throw new Error(
            "useUpdateTeacher must be used within UpdateTeacherProvider"
        );
    }
    return context;
};
